import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Select,
  Space,
  DatePicker,
  Avatar,
  Badge,
  Row,
  Col,
  Input,
  Modal,
  Spin,
  message,
  Card,
} from "antd";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import Styled from "styled-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { CALL_API } from "../../../Common/api";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { shipmentType } from "../../../Common/constant";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import { Tab } from "../../../Reusablecomponents/Tab";
import { ButtonFilled } from "../../../Reusablecomponents/UIButtons";
import ReusablePagination from "../../../Reusablecomponents/ReusablePagination";
import filtericon from "../../../assets/images/filter-svgrepo-com (1).svg";
import ExportIcon from "../../../assets/images/export-svgrepo-com (1).svg";
import { fetchNexusAssets, fetchNexusFences } from "../../TrackRightAI/nexusTrakingApi";
import NexusConsignmentReport from "./NexusConsignmentReport";
import { withNexusReportShell } from "../NexusReportShell";

dayjs.extend(utc);
dayjs.extend(timezone);

// Nexus Trip Report: same layout as the AI Trip Report, built from the Nexus
// trip tables (POST nexus/tripreport).

const Styledashboard = Styled.div`
  .Custom { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 20px; gap: 20px; }
  .dropstyle { margin-top: 0 !important; width: 251px !important; }
  .dropstyle1 { height: 36px; width: 251px !important; }
  .head { margin-bottom: 5px; color: #5f6d8c; height: 29px; }
  .header-container { display: flex; justify-content: space-between; align-items: center; width: 100%; }
  .chart-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
  .hover-image { width: 22px; height: 22px; }
  .total-mins { color: #363636; font-size: 20px; font-weight: 600; }
`;

const TRIP_STATUS = {
  notStarted: "Not Started",
  inProgress: "In Progress",
  completed: "Completed",
};

const formatMinutes = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const total = Math.round(Number(value));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const formatPickup = (value) =>
  value ? dayjs.utc(value).tz("Africa/Nairobi").format("DD/MM/YYYY hh:mm A") : "-";

const tripTypeLabel = (v) => (v === 1 ? "Local" : v ? "Safari" : "-");

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const { RangePicker } = DatePicker;
const { Option } = Select;

const NexusTripReport = () => {
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const transporterList = useSelector((state) => state?.authTracking?.transporterList);
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.AITRPT) && reduxPermissions.AITRPT.includes("EX"),
    [reduxPermissions],
  );

  const [form] = Form.useForm();
  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [filterValues, setFilterValues] = useState({});
  const [filterCount, setFilterCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({ data: [], total: 0, summary: {}, statusByType: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableSort, setTableSort] = useState({ field: null, order: null });
  const [CSVList, setCSVList] = useState([]);
  const [CSVLoader, setCSVLoader] = useState(false);
  const csvLinkEl = useRef();

  // filter options (TraKing vehicles / fences of the logged-in company)
  const [vehicles, setVehicles] = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [consignees, setConsignees] = useState([]);
  useEffect(() => {
    fetchNexusAssets()
      .then((r) => setVehicles(r.response || []))
      .catch(() => setVehicles([]));
    if (userInfo?.MCID === 59 || !userInfo?.NexusCompId) return;
    fetchNexusFences("Consignor", userInfo.NexusCompId).then(setConsignors).catch(() => setConsignors([]));
    fetchNexusFences("Consignee", userInfo.NexusCompId).then(setConsignees).catch(() => setConsignees([]));
  }, [userInfo?.MCID, userInfo?.NexusCompId]);

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const requestBody = useCallback(
    (page, size) => ({
      dateFrom: dayjs(selectedDate[0]).format("YYYY-MM-DD"),
      dateTo: dayjs(selectedDate[1]).format("YYYY-MM-DD"),
      page,
      pageSize: size,
      vehicle: filterValues?.vehicleno || "",
      consigner: filterValues?.consignor || "",
      transporter: filterValues?.transporter || "",
      consignee: filterValues?.fence || "",
      consignCode: filterValues?.consigncode || "",
      tripCode: filterValues?.trip || "",
      tripType: filterValues?.triptype || "",
      shipmentType: filterValues?.shipment || "",
    }),
    [selectedDate, filterValues],
  );

  const fetchReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/tripreport", requestBody(currentPage, pageSize));
      setReport({
        data: data?.data || [],
        total: data?.total || 0,
        summary: data?.summary || {},
        statusByType: data?.statusByType || [],
      });
    } catch (error) {
      console.error("[NexusTripReport] fetch error:", error);
      message.error("Unable to load the trip report.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, currentPage, pageSize, requestBody]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // a new filter or date range starts from the first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues, selectedDate]);

  const s = report.summary || {};
  const total = s.total || 0;
  const pct = (n) => (total ? ((n / total) * 100).toFixed(1) : "0.0");

  const avgLoading = s.avgLoadingMinutes ?? 0;
  const avgOffloading = s.avgOffloadingMinutes ?? 0;
  const avgTimeInMinutes = avgLoading + avgOffloading;
  const timingValues = [
    { label: "Loading Cycle", color: "rgb(13,178,24)", time: avgLoading },
    { label: "Offloading Cycle", color: "rgb(190,0,104)", time: avgOffloading },
  ];
  const chartLegendData = timingValues.map((item) => ({
    ...item,
    percentage: avgTimeInMinutes > 0 ? `${((item.time / avgTimeInMinutes) * 100).toFixed(2)}%` : "0.00%",
    time: formatMinutes(item.time),
  }));
  const tripTimingData = timingValues.map((item) => ({ name: item.label, value: item.time, color: item.color }));

  const statusChart = ["notStarted", "inProgress", "completed"].map((key) => {
    const local = report.statusByType.find((t) => t.tripType === 1) || {};
    const safari = report.statusByType.filter((t) => t.tripType !== 1);
    return {
      name: TRIP_STATUS[key],
      Local: local[key] ?? 0,
      Safari: safari.reduce((sum, t) => sum + (t[key] ?? 0), 0),
    };
  });

  const sortRows = (rows) => {
    if (!tableSort.field) return rows;
    const numeric = ["gpskms", "carbonfootprint", "LoadingCycle", "OffloadingCycle", "DeliveryTime", "triptype"];
    return [...rows].sort((a, b) => {
      let av = a[tableSort.field];
      let bv = b[tableSort.field];
      if (numeric.includes(tableSort.field)) {
        const r = (Number(av) || 0) - (Number(bv) || 0);
        return tableSort.order === "ascend" ? r : -r;
      }
      const r = String(av ?? "").localeCompare(String(bv ?? ""));
      return tableSort.order === "ascend" ? r : -r;
    });
  };

  const col = (title, dataIndex, render) => ({
    title,
    dataIndex,
    key: dataIndex,
    sorter: true,
    sortOrder: tableSort.field === dataIndex ? tableSort.order : null,
    render: render || ((v) => v ?? "-"),
  });

  const columns = [
    col("Trip Code", "tripcode", (v) => v || "-"),
    col("Pick Up Date and Time", "starttime", formatPickup),
    col("Vehicle", "vehiclename", (v) => v || "-"),
    col("Carbon Footprint (KgCo2)", "carbonfootprint", (v) => (v != null && v !== "" ? v : "-")),
    col("Driver Name", "drivername", (v) => v || "-"),
    col("ODO(Kms)", "gpskms", (v) => (v != null && v !== "" ? Number(v).toFixed(2) : "-")),
    col("Trip Type", "triptype", tripTypeLabel),
    col("Loading Cycle", "LoadingCycle", formatMinutes),
    col("Offloading Cycle", "OffloadingCycle", formatMinutes),
    col("Delivery Time", "DeliveryTime", formatMinutes),
  ];

  const prepareCSV = async () => {
    try {
      setCSVLoader(true);
      const { data } = await CALL_API("POST", "nexus/tripreport", requestBody(1, 5000));
      const rows = sortRows(data?.data || []);
      if (!rows.length) {
        message.error("No data available to download.");
        return;
      }
      setCSVList(
        rows.map((r) => ({
          "Trip Code": r.tripcode || "-",
          "Pick Up Date and Time": formatPickup(r.starttime),
          Vehicle: r.vehiclename || "-",
          "Carbon Footprint (KgCo2)": r.carbonfootprint ?? "-",
          "Driver Name": r.drivername || "-",
          "ODO(Kms)": r.gpskms != null && r.gpskms !== "" ? Number(r.gpskms).toFixed(2) : "-",
          "Trip Type": tripTypeLabel(r.triptype),
          "Loading Cycle": formatMinutes(r.LoadingCycle),
          "Offloading Cycle": formatMinutes(r.OffloadingCycle),
          "Delivery Time": formatMinutes(r.DeliveryTime),
          Consignor: r.ConsignorName || "-",
          Consignee: r.ConsigneeName || "-",
          Transporter: r.TransporterName || "-",
          "Container No": r.ContainerNo || "-",
          "Delivery Note": r.DeliveryNo || "-",
        })),
      );
      setTimeout(() => csvLinkEl.current?.link?.click(), 300);
    } catch (error) {
      console.error("[NexusTripReport] export error:", error);
      message.error("Unable to export the trip report.");
    } finally {
      setCSVLoader(false);
    }
  };

  const statBox = (label, value, sub) => (
    <div style={{ textAlign: "center", flex: 1 }}>
      <div style={{ fontSize: "18px", color: "#363636", marginBottom: "8px" }}>{label}</div>
      <div style={{ fontSize: "24px", fontWeight: "bold", color: "#101010" }}>{value}</div>
      {sub != null && <div style={{ fontSize: "14px", color: "#5F6D8C" }}>{sub}</div>}
    </div>
  );
  const divider = (
    <div className="nexus-stat-divider" style={{ width: "1px", height: "50px", backgroundColor: "#E0E0E0", margin: "0 20px" }} />
  );
  const barStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#F9F9F9",
    padding: "20px 30px",
    borderRadius: "8px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  };

  return (
    <Styledashboard>
      <div>
        <div className="total" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div className="head" style={{ flex: 1 }}>Overview</div>
          <div className="Custom" style={{ display: "flex", gap: "20px" }}>
            <div className="dropstyle">
              <Select value={duration} onChange={setDuration} className="plc" style={{ width: "100%" }}>
                {Duration.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="dropstyle1">
              <Space direction="vertical" size={12}>
                <RangePicker value={selectedDate} onChange={(d) => d && setSelectedDate(d)} />
              </Space>
            </div>
            <div>
              <Badge count={filterCount} className="badg" onClick={() => setIsModalOpen(true)}>
                <Avatar shape="circle" size="medium" src={filtericon} className="filterstyle" style={{ cursor: "pointer" }} />
              </Badge>
            </div>
          </div>
        </div>

        <Spin spinning={loading}>
          <div className="nexus-stat-bar" style={barStyle}>
            {statBox("Total No. of Trips", total)}
            {divider}
            {statBox("Delivery", s.delivery || 0, `${pct(s.delivery || 0)}%`)}
            {divider}
            {statBox("Collection", s.collection || 0, `${pct(s.collection || 0)}%`)}
          </div>
        </Spin>

        <Styledashboard>
          <Spin spinning={loading}>
            <Row gutter={[12, 12]}>
              <Col xl={12} style={{ marginTop: "20px" }}>
                <div id="Export Chart">
                  <Card style={{ height: "410px" }}>
                    <div style={{ flex: "1 1 500px", borderRadius: "8px", padding: "20px", maxWidth: "100%" }}>
                      <div className="chart-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0px" }}>
                        <p className="pb-0 mb-0" style={{ fontWeight: 500, fontSize: "16px" }}>Trip Status</p>
                        <img
                          src={ExportIcon}
                          alt="Export"
                          style={{
                            width: "20px",
                            height: "20px",
                            cursor: exportAllowed ? "pointer" : "not-allowed",
                            backgroundColor: "#EBF7ED",
                            borderRadius: "6px",
                          }}
                          onClick={() => {
                            if (!exportAllowed) return;
                            handleDownloadChart("Export Chart", "Trip Status");
                          }}
                        />
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart layout="vertical" data={statusChart} margin={{ top: 50, right: 20, left: -10, bottom: 5 }} barCategoryGap="10%">
                          <Legend verticalAlign="top" align="right" />
                          <Tooltip />
                          <XAxis type="number" tickLine={false} axisLine={false} allowDecimals={false} />
                          <YAxis dataKey="name" type="category" width={150} tickLine={false} axisLine={false} />
                          <Bar dataKey="Safari" stackId="a" fill="#FFD464" />
                          <Bar dataKey="Local" stackId="a" fill="#7064F5" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </div>
              </Col>
              <Col xl={12} style={{ marginTop: "20px" }}>
                <div id="Export Chart2">
                  <Card className="column_one">
                    <div className="chart-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <p className="pb-0 mb-0" style={{ fontWeight: 500, fontSize: "16px" }}>Trip Timing (HH:MM)</p>
                      <img
                        src={ExportIcon}
                        alt="Export"
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: exportAllowed ? "pointer" : "not-allowed",
                          backgroundColor: "#EBF7ED",
                          borderRadius: "6px",
                        }}
                        onClick={() => {
                          if (!exportAllowed) return;
                          handleDownloadChart("Export Chart2", "Trip Timing");
                        }}
                      />
                    </div>

                    <div className="timing_center_wrapper">
                      <ResponsiveContainer width="100%" height={290}>
                        <PieChart>
                          <Pie
                            data={tripTimingData}
                            startAngle={180}
                            endAngle={0}
                            innerRadius={80}
                            outerRadius={130}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                          >
                            {tripTimingData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="timing_center_wrapper_two">
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <div className="total-mins">{formatMinutes(avgTimeInMinutes)} </div>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "0px" }}>
                      {chartLegendData.map((item, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: "17px", height: "17px", borderRadius: "50%", backgroundColor: item.color }}></div>
                          <div style={{ flex: 1 }}>{item.label}</div>
                          <div>
                            {item.time} ({item.percentage})
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </Col>
            </Row>
          </Spin>
        </Styledashboard>

        <br />
        <div className="header-container">
          <div className="head">{report.total} records found.</div>
          <div>
            <Spin spinning={CSVLoader}>
              <img
                src={ExportIcon}
                style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
                alt="Export"
                className="hover-image"
                onClick={() => {
                  if (!exportAllowed) return;
                  prepareCSV();
                }}
              />
            </Spin>
            <CSVLink data={CSVList} filename={"NexusTripReport.csv"} ref={csvLinkEl} />
          </div>
        </div>
        <br />

        <StyledTable
          dataSource={sortRows(report.data).map((r) => ({ ...r, key: r.tripid }))}
          columns={columns}
          pagination={false}
          loading={loading}
          onChange={(pagination, filters, sorter) =>
            setTableSort({ field: sorter.field, order: sorter.order })
          }
        />

        <Modal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          width={900}
          footer={
            <ButtonFilled
              onClick={() => {
                form.resetFields();
                setFilterValues({});
                setFilterCount(0);
              }}
            >
              Clear All
            </ButtonFilled>
          }
        >
          <h6 className="Filter_for_clickablerow">Filter ({filterCount})</h6>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, all) => {
              setFilterValues(all);
              setFilterCount(Object.values(all).filter((v) => v && v.length !== 0).length);
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item label="Vehicle No" name="vehicleno">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={vehicles.map((a) => ({ value: a.asset_id, label: a.asset_name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consignor" name="consignor">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={consignors.map((f) => ({ value: String(f.geofence_id), label: f.name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Transporter" name="transporter">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={transporterList?.map((item) => ({ value: item.ID, label: item.Name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consignee" name="fence">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={consignees.map((f) => ({ value: f.name, label: f.name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consign Code" name="consigncode">
                  <Input className="rounded-pill" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Trip Code" name="trip">
                  <Input className="rounded-pill" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Trip Type" name="triptype">
                  <Select className="w-100" allowClear>
                    <Option value={2}>Safari</Option>
                    <Option value={1}>Local</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Shipment Type" name="shipment">
                  <Select
                    className="w-100"
                    allowClear
                    options={shipmentType?.map((item) => ({ label: item.name, value: item.value }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>

      <ReusablePagination
        pageSize={pageSize}
        handlePageSizeChange={(v) => {
          setPageSize(v);
          setCurrentPage(1);
        }}
        currentPage={currentPage}
        total={report.total}
        handlePageChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
    </Styledashboard>
  );
};

const NexusTripReportPage = () => {
  const [activekey, setActivekey] = useState("1");
  return (
    <div>
      <Tab
        defaultActiveKey="1"
        items={[
          { label: "Trip Reports", key: "1" },
          { label: "Consignment Reports", key: "2" },
        ]}
        onChange={setActivekey}
      />
      <div className="px-3" style={{ marginTop: "16px" }}>
        {activekey === "1" && <NexusTripReport />}
        {activekey === "2" && <NexusConsignmentReport />}
      </div>
    </div>
  );
};

export default withNexusReportShell(NexusTripReportPage);
